/**
 * SEO Component
 * 
 * Manages meta tags, Open Graph, Twitter Cards, and JSON-LD schema
 * for optimal search engine optimization
 */

import { Helmet } from 'react-helmet-async';
import { siteConfig } from '@/lib/site-config';
import { useSiteConfig } from '@/lib/useSiteConfig';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  /** Page title (will be combined with site name) */
  title?: string;
  /** Meta description */
  description?: string;
  /** Canonical URL (defaults to current location) */
  canonical?: string;
  /** Open Graph image URL */
  image?: string;
  /** Content type for Open Graph */
  type?: 'website' | 'article' | 'profile';
  /** Article published time (ISO 8601) */
  publishedTime?: string;
  /** Article modified time (ISO 8601) */
  modifiedTime?: string;
  /** Article author */
  author?: string;
  /** Article tags/categories */
  tags?: string[];
  /** Keywords for meta tag */
  keywords?: string[];
  /** Whether to index this page */
  noindex?: boolean;
  /** Whether to follow links */
  nofollow?: boolean;
  /** Language (defaults to 'en') */
  lang?: string;
  /** Alternate language versions */
  alternateLanguages?: Array<{ lang: string; url: string }>;
  /** Structured data (JSON-LD) */
  structuredData?: Record<string, unknown>;
}

export const SEO = ({
  title,
  description,
  canonical,
  image,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  tags = [],
  keywords = [],
  noindex = false,
  nofollow = false,
  lang = 'en',
  alternateLanguages = [],
  structuredData,
}: SEOProps) => {
  const location = useLocation();
  const config = useSiteConfig();
  const siteName = config.organizationName;
  const siteUrl = typeof window !== 'undefined' 
    ? `${window.location.origin}${location.pathname}`
    : '';
  
  // Build full title
  const fullTitle = title 
    ? `${title} | ${siteName}`
    : `${siteName} - ${config.tagline}`;
  
  // Use provided canonical or default to current URL
  const canonicalUrl = canonical || siteUrl;
  
  // Default image (site logo or hero image)
  const ogImage = image || `${siteUrl}/og-image.jpg`;
  
  // Default description
  const metaDescription = description || config.description || config.tagline;
  
  // Combine keywords - ensure they're arrays
  const keywordsArray = Array.isArray(keywords) ? keywords : (keywords ? [String(keywords)] : []);
  const tagsArray = Array.isArray(tags) ? tags : (tags ? [String(tags)] : []);
  
  // Also include default keywords from config if available
  const configKeywords = config.seo?.defaultKeywords;
  const configKeywordsArray = Array.isArray(configKeywords) 
    ? configKeywords 
    : (configKeywords ? [String(configKeywords)] : []);
  
  const allKeywords = [
    ...keywordsArray,
    ...tagsArray,
    ...configKeywordsArray,
    siteName,
  ].filter(Boolean).join(', ');

  // Generate JSON-LD structured data
  const generateStructuredData = () => {
    const baseStructuredData = {
      '@context': 'https://schema.org',
      '@type': type === 'article' ? 'Article' : type === 'profile' ? 'ProfilePage' : 'WebPage',
      headline: title || siteName,
      description: metaDescription,
      image: ogImage,
      url: canonicalUrl,
      publisher: {
        '@type': 'Organization',
        name: siteName,
        url: siteUrl.split(location.pathname)[0],
      },
    };

    if (type === 'article') {
      return {
        ...baseStructuredData,
        datePublished: publishedTime,
        dateModified: modifiedTime || publishedTime,
        author: author ? {
          '@type': 'Person',
          name: author,
        } : undefined,
        keywords: allKeywords,
      };
    }

    return structuredData || baseStructuredData;
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {allKeywords && <meta name="keywords" content={allKeywords} />}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      {(noindex || nofollow) && (
        <meta 
          name="robots" 
          content={`${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`} 
        />
      )}
      
      {/* Language */}
      <html lang={lang} />
      
      {/* Alternate Languages */}
      {alternateLanguages.map((alt) => (
        <link key={alt.lang} rel="alternate" hrefLang={alt.lang} href={alt.url} />
      ))}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={lang} />
      
      {/* Article-specific OG tags */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {author && <meta property="article:author" content={author} />}
          {tags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(generateStructuredData(), null, 2)}
      </script>
    </Helmet>
  );
};

/**
 * SEO hook for easy usage in components
 */
export const useSEO = (props: SEOProps) => {
  return <SEO {...props} />;
};

export default SEO;




